import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'
import { ToastContext, type Toast, type ToastTone } from './toast-context'

/* Transient confirmation, in one place.

   A booking that succeeds has to say so somewhere other than the form it
   just closed, and a booking that fails has to say so without the visitor
   losing what they typed. Both are this component.

   Live region, not just a coloured box: `role="status"` with an assertive
   politeness on errors means a screen reader hears the outcome too, which a
   silently appearing div does not provide. */

const LIFETIME = 6000

let nextId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const toast = useCallback((next: Omit<Toast, 'id'>) => {
    nextId += 1
    const id = nextId
    /* Three at a time. A stack taller than that covers the page it is
       reporting on, and nobody reads the fourth one anyway. */
    setToasts((current) => [...current.slice(-2), { ...next, id }])
  }, [])

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

const TONES: Record<ToastTone, { ring: string; icon: typeof CheckCircle2; colour: string }> = {
  success: { ring: 'border-mint/40', icon: CheckCircle2, colour: 'text-mint-deep' },
  error: { ring: 'border-lime/40', icon: AlertTriangle, colour: 'text-lime-deep' },
  info: { ring: 'border-line', icon: Info, colour: 'text-silver' },
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: number) => void
}) {
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      /* Bottom on a phone, where nothing important sits and the thumb can
         reach the dismiss; top-right on a desktop, out of the content. */
      className="pointer-events-none fixed inset-x-4 bottom-4 z-100 flex flex-col gap-3 sm:inset-x-auto sm:top-24 sm:right-6 sm:bottom-auto sm:w-[380px]"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  )
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: Toast
  onDismiss: (id: number) => void
}) {
  const { ring, icon: Icon, colour } = TONES[toast.tone]

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), LIFETIME)
    return () => clearTimeout(timer)
  }, [toast.id, onDismiss])

  return (
    <div
      role="status"
      aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
      className={`pointer-events-auto flex animate-rise items-start gap-3 rounded-2xl border ${ring} bg-raised p-4 shadow-lift`}
    >
      <Icon aria-hidden="true" strokeWidth={1.8} className={`mt-0.5 size-5 shrink-0 ${colour}`} />

      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-platinum">{toast.title}</p>
        {toast.body ? (
          <p className="mt-1 text-[13px] leading-relaxed text-steel">{toast.body}</p>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        className="grid size-7 shrink-0 place-items-center rounded-full text-dim transition hover:bg-surface hover:text-platinum"
      >
        <X aria-hidden="true" strokeWidth={2} className="size-3.5" />
      </button>
    </div>
  )
}
